import { useState, useCallback } from 'react';

import { isNotNullOrUndefined, isStringNotBlank } from 'utils/utils';

type UseCopyToClipboardOptions = {
  onSuccessfulCopy?: (text: string | undefined) => void;
  onFailedCopy?: (text: string | undefined, error: string) => void;
  showFeedbackDuration?: number;
};

type UseCopyToClipboardReturn = {
  onClick: (textToCopy: string) => Promise<void>;
  isCopying: boolean;
  isError: boolean;
  feedbackMessage: string | null;
};

const DEFAULT_FEEDBACK_DURATION = 1000;

export const copyTextToClipboard = async (text: string | undefined) => navigator.clipboard.writeText(text ?? '');

export const useCopyToClipboard = (options?: UseCopyToClipboardOptions): UseCopyToClipboardReturn => {
  const { onSuccessfulCopy, onFailedCopy, showFeedbackDuration = DEFAULT_FEEDBACK_DURATION } = options ?? {};
  const [isCopying, setIsCopying] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);

  const onClick = useCallback(
    async (textToCopyParam: string) => {
      // Prevent multiple copy operations from happening simultaneously
      if (isCopying) {
        return;
      }

      setIsCopying(true);
      setIsError(false);
      setFeedbackMessage(null);
      let success: boolean;
      let error = 'Unknown error';

      try {
        await copyTextToClipboard(textToCopyParam);
        success = true;
      } catch (e) {
        success = false;
        // eslint-disable-next-line no-console
        console.error('Error when copying to clipboard', e);
        if (isStringNotBlank((e as Error).message)) {
          error = (e as Error).message;
        }
      }

      if (success) {
        if (isNotNullOrUndefined(onSuccessfulCopy)) {
          onSuccessfulCopy(textToCopyParam);
        }
        setFeedbackMessage('Copied!');
      } else {
        if (isNotNullOrUndefined(onFailedCopy)) {
          onFailedCopy(textToCopyParam, error);
        }
        setIsError(true);
        setFeedbackMessage('Error!');
      }

      // Clear feedback after a delay
      setTimeout(() => {
        setFeedbackMessage(null);
        setIsCopying(false);
        setIsError(false);
      }, showFeedbackDuration);
    },
    [onSuccessfulCopy, onFailedCopy, showFeedbackDuration, isCopying],
  );

  return {
    onClick,
    isCopying,
    isError,
    feedbackMessage,
  };
};
